import get from 'lodash/get';
import compact from 'lodash/compact';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ClampedText from 'react-dotdotdot';
import classNames from 'clsx';
import { behaviors as GuideItemBehaviors } from 'src/common/utils/guideItemTypes';
import { getDestInfoUrlObj } from 'src/common/utils/guideItem/getGuideItemPathname';
import PremiumLogo from '../../subscription/icons/PremiumLogo';
import { closeNowPlayingDialog } from '../../../actions/dialog';
import UnderlineLink from '../../shared/link/UnderlineLink';
import { SEE_ALL } from '../../../constants/localizations/containerItems';
import chevronCss from '../../shared/link/chevron-link.module.scss';
import commonCss from '../../../styles/common.module.scss';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import getGuideItemProducts from '../../../utils/guideItem/getGuideItemProducts';
import css from './container-title.module.scss';

const HEADERLESS_LIST = 'HeaderlessList';

// exported for testing
export const ContainerTitle = ({
  containerItem,
  getStyles,
  className,
  actions,
  isNowPlayingDialogOpen,
  containerTitle,
}) => {
  const {
    containerNavigation = {},
    presentation = {},
    style = '',
    subtitle,
  } = containerItem;
  const title = containerTitle || containerItem.title;
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const { titleVisible, titleSize, subtitleVisible } = getStyles(style);
  const hideTitle = containerTitle
    ? false
    : presentation.layout === HEADERLESS_LIST ||
      titleVisible === false ||
      !title;
  const hideSubtitle = subtitleVisible === false || !subtitle;

  if (hideTitle) {
    return null;
  }

  const guideId = get(containerItem, 'children[0].guideId', '');
  const guideImage = get(containerItem, 'children[0].image', '');
  const guideTitle = get(containerItem, 'children[0].title', '');
  const guideProducts = compact(
    getGuideItemProducts(containerItem?.children?.[0]),
  );

  const hasMoreItems = containerNavigation.type === GuideItemBehaviors.browse;
  let linkTo;

  if (hasMoreItems) {
    linkTo = {
      ...getDestInfoUrlObj(containerNavigation.destinationInfo),
      state: { itemToken: get(containerNavigation, 'context.itemToken') },
    };
  }

  const containerTitleContainerClassNames = classNames(
    css.containerTitleContainer,
    {
      [css.spaceBetween]: hasMoreItems,
    },
    className,
  );

  const titleHeader = (
    <ClampedText
      className={classNames(css.titleHeader, {
        [css[`titleHeader${titleSize}`]]: titleSize,
      })}
      clamp={2}
    >
      {title}
    </ClampedText>
  );

  const subtitleHeader = !hideSubtitle && (
    <ClampedText className={css.subtitleHeader} clamp={2}>
      {subtitle}
    </ClampedText>
  );

  const handleClick = () => {
    if (isNowPlayingDialogOpen) {
      actions.closeNowPlayingDialog();
    }
  };

  return (
    <div
      className={containerTitleContainerClassNames}
      data-testid="containerTitleContainer"
      data-title={title}
    >
      {hasMoreItems ? (
        <>
          <div className={css.titleContainer} data-testid="titleContainer">
            <Link
              to={linkTo}
              onClick={handleClick}
              className={commonCss.inkLink}
            >
              {titleHeader}
              {subtitleHeader}
            </Link>
          </div>
          <div className={css.browseLink} data-testid="browseLink">
            <Link
              to={linkTo}
              onClick={handleClick}
              className={classNames(commonCss.inkLink, css.displayChevron)}
            >
              <i
                className={classNames(
                  chevronCss.chevronRight,
                  chevronCss.chevronRightBackground,
                  css.chevron,
                )}
              />
            </Link>
            <UnderlineLink
              to={linkTo}
              onClick={handleClick}
              className={commonCss.inkLink}
              isDropDown
            >
              <span className={css.seeAll}>{getLocalizedText(SEE_ALL)}</span>
            </UnderlineLink>
          </div>
        </>
      ) : (
        <div className={css.titleContainer} data-testid="titleContainer">
          {titleHeader}
          {subtitleHeader}
        </div>
      )}
      {presentation.layout === 'Locked' && (
        <div className={css.premiumLogo}>
          <PremiumLogo
            guideId={guideId}
            products={guideProducts}
            image={guideImage}
            title={guideTitle}
          />
        </div>
      )}
    </div>
  );
};

ContainerTitle.propTypes = {
  containerItem: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  isNowPlayingDialogOpen: PropTypes.bool.isRequired,
  getStyles: PropTypes.func,
  className: PropTypes.string,
  containerTitle: PropTypes.string,
};

ContainerTitle.defaultProps = {
  getStyles: () => ({}),
};

function mapStateToProps(state) {
  return {
    isNowPlayingDialogOpen: get(state, 'dialog.isNowPlayingDialogOpen', false),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ closeNowPlayingDialog }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ContainerTitle);
